import { navigate } from 'gatsby';
import { useContext, useRef } from 'react';

import ROUTES from '../routes';
import validationRules, {
  Model,
} from '../components/change-password/ChangePassword.rules';
import { updatePasswordMutation } from '../queries/changePassword.query';
import { useApolloClient } from '@apollo/react-hooks';
import useFormState from './useFormState';
import { NotificationsContext } from '../providers/NotificationsProvider';

export const defaultFormValues: Model = {
  password: '',
};
import { useTranslation } from '../hooks/useTranslation';
import useAuth from './useAuth';
import { useLocation } from '@reach/router';

type RouterState = {
  redirectUrl?: string;
};

export default () => {
  const apolloClient = useApolloClient();
  const notificationContext = useContext(NotificationsContext);
  const { t } = useTranslation();
  const { signInWithCustomToken } = useAuth();
  const passwordUpdatedRef = useRef(false);
  const signInTokenRef = useRef<string | null>(null);
  const location = useLocation();
  const redirectUrl =
    ((location.state || {}) as RouterState)?.redirectUrl || undefined;

  const form = useFormState(defaultFormValues, {
    validate: validationRules,
    validationContext: {
      apolloClient,
    },
  });

  const doSubmit = async (
    data: any,
    encryptedPersonId: string | null,
  ): Promise<void> => {
    passwordUpdatedRef.current = false;
    signInTokenRef.current = null;
    await apolloClient
      .mutate({
        mutation: updatePasswordMutation,
        variables: {
          payload: {
            password: data.password,
            encryptedPersonId: encryptedPersonId,
          },
        },
      })
      .then(result => {
        const updatePasswordResponse = result?.data?.updatePassword;
        if (
          !updatePasswordResponse?.success ||
          !updatePasswordResponse.signInToken
        ) {
          onFailure();
        } else {
          passwordUpdatedRef.current = true;
          signInTokenRef.current = updatePasswordResponse.signInToken;
        }
      });
    return Promise.resolve();
  };

  const onFailure = (): void => {
    notificationContext.setState({
      isOpen: true,
      message: t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'),
      severity: 'error',
    });
  };

  const onSuccess = async (username: string): Promise<void> => {
    if (!passwordUpdatedRef.current) {
      return;
    }

    if (!username) {
      onFailure();
      return;
    }

    const signInToken = signInTokenRef.current;
    if (!signInToken) {
      onFailure();
      return;
    }

    try {
      await signInWithCustomToken(signInToken, username);
    } catch (error) {
      onFailure();
      return;
    }

    passwordUpdatedRef.current = false;

    notificationContext.setState({
      isOpen: true,
      severity: 'success',
      variant: 'filled',
      message: t('CHANGE_PASSWORD_SUCCESS'),
    });

    if (redirectUrl) {
      return navigate(redirectUrl);
    }

    return navigate(ROUTES.ACCOUNT);
  };

  return {
    form,
    doSubmit,
    onSuccess,
  };
};
